import { forwardRef, useMemo } from 'react';

import { ChevronDown } from '../../icons/lucide/ChevronDown';
import { Platform } from '../../platform';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { Button } from '../Button';
import type { IconComponent } from '../Button/Button.types';
import type { DropdownSelectorProps } from './DropdownSelector.types';
import {
  DROPDOWN_TO_BUTTON_SIZE,
  DROPDOWN_TRIGGER_LEFT_ICON_SIZE,
  DROPDOWN_TRIGGER_OVERRIDE,
  DROPDOWN_TRIGGER_TEXT_OVERRIDE,
  DROPDOWN_TRIGGER_VARIANT_OVERRIDE,
  OPEN_OVERRIDE,
} from './DropdownSelector.variants';

const IS_WEB = Platform.OS === 'web';

// Detached ref shape — see Button.tsx / MenuListItem.tsx: pnpm hoists multiple
// @types/react copies so `Ref<T>` is nominally distinct across consumers.
type AnyRef<T> = ((node: T | null) => void) | { current: T | null } | null;

/**
 * The shared "Drop down selector" trigger chip (Figma `1008:4306`) — a DS
 * `<Button>` restyled with the per-size radius / padding / gap / Medium type
 * from {@link DropdownSelector.variants}, a right-hand chevron that flips on
 * `open`, a leading icon sized 4 px larger than the chevron, and the "Opened"
 * pressed treatment.
 *
 * `forwardRef` so it slots into a Radix `Popover.Trigger asChild` on web (the
 * ref + injected `onClick`/`aria-*`/`data-state` flow straight through to the
 * underlying Button → Pressable). On native the caller wires `onPress` to open
 * a bottom sheet instead.
 *
 * Owned by neither popup semantics: `Dropdown` (actions) and the future
 * `Select` (value picker) both render this exact chip.
 */
function DropdownSelectorInner(
  {
    children,
    variant = 'secondary',
    size = 'm',
    open = false,
    disabled = false,
    fullWidth = false,
    leftIcon: LeftIcon,
    rightIcon: RightIconProp,
    onPress,
    muted = false,
    accessibilityLabel,
    testID,
    className,
    textClassName,
    webProps,
    nativeProps,
    animationDuration,
    animationEasing,
    animationBezier,
    disableAnimation,
    // Radix `Trigger asChild` (web) clones this element and injects
    // onClick / onPointerDown / aria-* / data-state; funnel them down to
    // Button → Pressable so the popover actually opens.
    ...injected
  }: DropdownSelectorProps & Record<string, unknown>,
  ref: AnyRef<HTMLElement>,
) {
  const buttonSize = DROPDOWN_TO_BUTTON_SIZE[size];

  // Button sizes both icon slots with one value; force the leading icon to the
  // larger per-band size while preserving the colour Button injects.
  const LeftIconForButton = useMemo<IconComponent | undefined>(() => {
    if (!LeftIcon) return undefined;
    const Base = LeftIcon;
    const forcedSize = DROPDOWN_TRIGGER_LEFT_ICON_SIZE[size];
    return function SizedLeftIcon(props: {
      color?: string;
      size?: number;
      strokeWidth?: number;
    }) {
      return <Base {...props} size={forcedSize} />;
    };
  }, [LeftIcon, size]);

  // Chevron in a rotation-aware shell. The transition is `web:`-gated — on
  // native NativeWind would upgrade a transitioned host to a Reanimated
  // component and crash (same lesson as Button/SegmentedButton).
  //
  // The ROTATION itself is web-class / native-style split for a second
  // reason: toggling a transform class on native re-parses the className at
  // render time, which races react-navigation's context on Fabric and takes
  // the screen down with a bogus "Couldn't find a navigation context" render
  // error when the toggle happens inside a FlashList (see SectionHeader).
  const ChevronForButton = useMemo<IconComponent>(() => {
    const Base = RightIconProp ?? ChevronDown;
    return function OpenAwareChevron(props: {
      color?: string;
      size?: number;
      strokeWidth?: number;
    }) {
      return (
        <View
          className={cn(
            'shrink-0 web:transition-transform web:duration-150 web:ease-out',
            IS_WEB && open && 'rotate-180',
          )}
          nativeProps={
            !IS_WEB && open ? { style: { transform: [{ rotate: '180deg' }] } } : undefined
          }>
          <Base {...props} />
        </View>
      );
    };
  }, [RightIconProp, open]);

  const triggerClassName = cn(
    // Push the chevron to the right edge; the label fills the middle. `!` beats
    // Button's base `justify-center` via twMerge.
    '!justify-between',
    // Pin the chip as a non-flexing item — Button copies `textClassName` onto
    // the container too, so the label's `flex-1` would otherwise leak onto the
    // chip and let a flex parent collapse its height. `w-full` (fullWidth) still
    // wins for width.
    '!flex-none',
    DROPDOWN_TRIGGER_OVERRIDE[size],
    DROPDOWN_TRIGGER_VARIANT_OVERRIDE[variant],
    open && OPEN_OVERRIDE[variant],
    className,
  );

  const triggerTextClassName = cn(
    // `flex-1` only when the chip is stretched: it makes the label fill the
    // row so `justify-between` pins the chevron to the far edge. A HUG-width
    // chip must NOT use it — on native, Yoga measures a basis-0 label at zero
    // width inside the content-sized chip, collapsing it to icon + chevron.
    fullWidth ? 'flex-1 text-left' : 'flex-none text-left',
    DROPDOWN_TRIGGER_TEXT_OVERRIDE[size],
    muted && '!text-secondary-400 ',
    textClassName,
  );

  return (
    <Button
      {...injected}
      ref={ref}
      variant={variant}
      size={buttonSize}
      fullWidth={fullWidth}
      disabled={disabled}
      leftIcon={LeftIconForButton}
      rightIcon={ChevronForButton}
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      className={triggerClassName}
      textClassName={triggerTextClassName}
      webProps={webProps}
      nativeProps={nativeProps}
      animationDuration={animationDuration}
      animationEasing={animationEasing}
      animationBezier={animationBezier}
      disableAnimation={disableAnimation}>
      {children}
    </Button>
  );
}

/** See Button.tsx for the forwardRef cast rationale (the `...injected` spread
 *  puts an index signature on the props, so we detach through `never`). */
export const DropdownSelector = forwardRef(
  DropdownSelectorInner as never,
) as unknown as React.ForwardRefExoticComponent<
  DropdownSelectorProps & { ref?: AnyRef<HTMLElement> }
>;
(DropdownSelector as { displayName?: string }).displayName = 'DropdownSelector';
